import CheckIcon from '@mui/icons-material/Check';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { Avatar, Badge, Box, Card, Grid, Link } from '@mui/material';
import { useEffect, useState } from 'react';
import { HiOutlineOfficeBuilding } from 'react-icons/hi';
import { ClientDTO, NoteDTO, getClientById } from 'wisestep-api';
import {
  WsIconProfile,
  WsIconTime,
  WsIconText,
  WsColors,
  WsTypoColors,
  WsTypoVariants,
  WsTypography,
  timeSince,
  WsIconVisa,
} from 'wisestep-base';

import { NotesParent } from '../../../components/communicationBar/Notes';
import { ClintPageBreadCrumb } from '../../../features/clientDetails/helper/clientDetailsHelper';
import getEncodedQueryParams from '../../../utils/getEncodedQueryParams';
import { stripHtmlTags } from '../helper/NotesHelper';

interface INotesCardProps {
  note: NoteDTO;
  onClick?: () => void;
  isSelected: boolean;
}

const NotesCard = ({ note, onClick, isSelected }: INotesCardProps) => {
  const [parentInfo, setParentInfo] = useState<ClientDTO>();

  useEffect(() => {
    const fetchData = async () => {
      if (note.client?.parentId) {
        const response = await getClientById(note.client?.parentId as string);
        setParentInfo(response.data.data);
      }
    };
    fetchData();
  }, [note.client?.parentId]);

  const handleNavigation = (id, name, notePath) => {
    switch (notePath) {
      case NotesParent.CLIENT:
        return `/client-details?tabIndex=0&isProfile=true&isChild=${
          note.client.parentId != null ? false : true
        }&name=${parentInfo?.name}&clientId=${id}&breadCrumbList=${getEncodedQueryParams(
          ClintPageBreadCrumb['CLIENTS']
        )}`;
      case NotesParent.CLIENTCONTACT:
        return `/${id}/client-contact-details?isChild=true&isProfile=true&name=${name}`;
      default:
        return `/client-details?tabIndex=0&isProfile=true&isChild=false&name=${name}&clientId=${id}&breadCrumbList=${getEncodedQueryParams(
          ClintPageBreadCrumb['CLIENTS']
        )}`;
    }
  };

  return (
    <Box my={1} mx={2} pt={2}>
      <Badge
        badgeContent={
          <Avatar sx={{ bgcolor: WsColors.TheBlues500, width: 20, height: 20 }}>
            <CheckIcon fontSize="small" />
          </Avatar>
        }
        invisible={!isSelected}
        sx={{
          width: '100%',
        }}
      >
        <Card
          sx={{
            width: '100%',
            cursor: 'pointer',
          }}
          onClick={onClick}
        >
          <Box
            sx={{
              border: 'solid 1px',
              borderRadius: '1px',
              backgroundColor: isSelected ? WsColors.TheBlue75 : WsColors.White,
              borderColor: isSelected ? WsColors.TheBlues300 : WsColors.BorderGrey,
              width: '100%',
              padding: 2,
            }}
          >
            <Grid container direction="column" spacing={0.5}>
              <Grid item>
                <WsTypography
                  color={WsTypoColors.BLACK}
                  variant={WsTypoVariants.TITLE}
                  bold
                  cursor="pointer"
                >
                  {note.heading}
                </WsTypography>
              </Grid>
              <Grid item>
                <WsTypography
                  color={WsTypoColors.BLACK}
                  variant={WsTypoVariants.BODY1}
                  cursor="pointer"
                >
                  {note?.description?.length > 45
                    ? `${stripHtmlTags(note?.description).slice(0, 45)}...`
                    : stripHtmlTags(note?.description)}
                </WsTypography>
              </Grid>
              <Grid item container direction="row" columnSpacing={0.5}>
                <Grid item sx={{ ml: -0.5 }}>
                  {note.noteType && (
                    <WsTypography
                      color={WsTypoColors.SECONDARY}
                      variant={WsTypoVariants.BODY1}
                      hoverText={'Note Type'}
                      cursor="default"
                    >
                      <Grid container alignItems="center">
                        <WsIconVisa color={WsColors.TertiaryShades900} />
                        {note.noteType}
                      </Grid>
                    </WsTypography>
                  )}
                </Grid>
                <Grid item>
                  {note.client && (
                    <Link
                      underline="none"
                      href={handleNavigation(note.client.id, note.client.name, NotesParent.CLIENT)}
                    >
                      <WsTypography
                        color={WsTypoColors.SECONDARY}
                        variant={WsTypoVariants.BODY1}
                        hoverText={note.client.parentId != null ? 'Child Client' : 'Client'}
                        cursor="pointer"
                      >
                        <Grid container alignItems="center">
                          <HiOutlineOfficeBuilding
                            size={17}
                            color={WsColors.TertiaryShades500}
                            style={{ marginRight: 3 }}
                          />
                          {note?.client?.name.length > 10
                            ? `${(note?.client?.name ?? '').slice(0, 10)}...`
                            : note?.client?.name}
                        </Grid>
                      </WsTypography>
                    </Link>
                  )}
                </Grid>
                <Grid item>
                  {note.clientContact && (
                    <Link
                      underline="none"
                      href={handleNavigation(
                        note.clientContact.id,
                        note.client.name,
                        NotesParent.CLIENTCONTACT
                      )}
                    >
                      <WsTypography
                        color={WsTypoColors.SECONDARY}
                        variant={WsTypoVariants.BODY1}
                        hoverText="Contact"
                        cursor="pointer"
                      >
                        <Grid container alignItems="center">
                          <PersonOutlineIcon
                            sx={{
                              marginRight: 0.3,
                              fontSize: 19,
                              color: WsColors.TertiaryShades500,
                            }}
                          />
                          {note?.clientContact?.firstName.length > 10
                            ? `${(note?.clientContact?.firstName ?? '').slice(0, 10)}...`
                            : note?.clientContact?.firstName}
                        </Grid>
                      </WsTypography>
                    </Link>
                  )}
                </Grid>
              </Grid>
              <Grid item ml={1}>
                {note.createdOn && (
                  <WsIconText
                    icon={<WsIconTime color={WsColors.TertiaryShades900} />}
                    text={`${timeSince(String(note.createdOn))}`}
                    textSize={WsTypoVariants.BODY1}
                    opacity={true}
                    popOverText={'Created On'}
                    textCursor="pointer"
                  />
                )}
                {note.createdByName && (
                  <WsIconText
                    icon={<WsIconProfile color={WsColors.TertiaryShades500} />}
                    text={note.createdByName}
                    textSize={WsTypoVariants.BODY1}
                    opacity={true}
                    popOverText={'Created By'}
                    textCursor="pointer"
                  />
                )}
              </Grid>
            </Grid>
          </Box>
        </Card>
      </Badge>
    </Box>
  );
};
export default NotesCard;
